import React, { useEffect, useState } from "react";
import MasterLayout from "../masterLayout/MasterLayout";
import Breadcrumb from "../components/Breadcrumb";
import DashboardFilter from "../components/DashboardFilter";
import AppointmentTable from "../components/AppointmentTable";

import { API_URL } from "..";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Spinner } from "react-bootstrap";

const DEFAULT_PAGINATION = {
  page: 1,
  limit: 25,
  total: 0,
  totalPages: 1,
};

const AppointmentStatusPage = () => {
  const { status } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [dateFilter, setDateFilter] = useState(
    searchParams.get("filter") || "all"
  );

  const [customRange, setCustomRange] = useState({
    startDate: searchParams.get("startDate") || "",
    endDate: searchParams.get("endDate") || "",
  });

  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");

  const [searchColumn, setSearchColumn] = useState(
    searchParams.get("searchColumn") || "all"
  );

  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);

  const getStatusTitle = () => {
    switch (status) {
      case "total":
        return "Total Jobs";
      case "un-assigned":
        return "UnAssigned Jobs";
      case "new":
        return "New Jobs";
      case "active":
        return "Active Jobs";
      case "completed":
        return "Completed Jobs";
      case "declined":
        return "Declined Jobs";
      case "closed":
        return "Closed Jobs";
      default:
        return "Jobs";
    }
  };

  const getApiStatus = () => {
    if (status === "total") return "all";
    return status || "all";
  };

  const updateUrlParams = ({
    filter = dateFilter,
    range = customRange,
    page = pagination.page,
    limit = pagination.limit,
    search = searchTerm,
    column = searchColumn,
  }) => {
    const params = new URLSearchParams();

    params.set("filter", filter);
    params.set("page", String(page));
    params.set("limit", String(limit));

    if (search && String(search).trim() !== "") {
      params.set("search", String(search).trim());
      params.set("searchColumn", column || "all");
    }

    if (filter === "custom") {
      if (range.startDate) {
        params.set("startDate", range.startDate);
      }

      if (range.endDate) {
        params.set("endDate", range.endDate);
      }
    }

    navigate(`/jobs/${status}?${params.toString()}`, {
      replace: false,
    });
  };

  const fetchAppointments = async ({
    page = 1,
    limit = 25,
    filter = "all",
    range = { startDate: "", endDate: "" },
    search = "",
    column = "all",
  }) => {
    setLoading(true);

    try {
      const params = new URLSearchParams();

      params.set("page", String(page));
      params.set("limit", String(limit));
      params.set("filter", filter);

      if (filter === "custom" && range.startDate && range.endDate) {
        params.set("startDate", range.startDate);
        params.set("endDate", range.endDate);
      }

      if (search && String(search).trim() !== "") {
        params.set("search", String(search).trim());
        params.set("searchColumn", column || "all");
      }

      /**
       * IMPORTANT:
       * Same updated backend controller use karna hai:
       * /api/job/fetch/:status
       *
       * total page ke liye status = all bhejna hai.
       */
      const response = await fetch(
        `${API_URL}/api/job/fetch/${getApiStatus()}?${params.toString()}`,
        {
          method: "GET",
          headers: {
            Accept: "*/*",
          },
        }
      );

      const result = await response.json();

      if (response.status === 200) {
        setData(result.job || result.jobs || []);
        setPagination(result.pagination || DEFAULT_PAGINATION);
      } else {
        setData([]);
        setPagination(DEFAULT_PAGINATION);
      }
    } catch (error) {
      console.error("Error fetching status jobs:", error);
      setData([]);
      setPagination(DEFAULT_PAGINATION);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const filterFromUrl = searchParams.get("filter") || "all";
    const startDateFromUrl = searchParams.get("startDate") || "";
    const endDateFromUrl = searchParams.get("endDate") || "";
    const pageFromUrl = Number(searchParams.get("page")) || 1;
    const limitFromUrl = Number(searchParams.get("limit")) || 25;
    const searchFromUrl = searchParams.get("search") || "";
    const searchColumnFromUrl = searchParams.get("searchColumn") || "all";

    const rangeFromUrl = {
      startDate: startDateFromUrl,
      endDate: endDateFromUrl,
    };

    setDateFilter(filterFromUrl);
    setCustomRange(rangeFromUrl);
    setSearchTerm(searchFromUrl);
    setSearchColumn(searchColumnFromUrl);

    fetchAppointments({
      page: pageFromUrl,
      limit: limitFromUrl,
      filter: filterFromUrl,
      range: rangeFromUrl,
      search: searchFromUrl,
      column: searchColumnFromUrl,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, searchParams.toString()]);

  const handleFilterChange = (newFilter) => {
    setDateFilter(newFilter);

    if (newFilter !== "custom") {
      updateUrlParams({
        filter: newFilter,
        range: {
          startDate: "",
          endDate: "",
        },
        page: 1,
        limit: pagination.limit,
        search: searchTerm,
        column: searchColumn,
      });
    }
  };

  const handleCustomApply = () => {
    if (!customRange.startDate || !customRange.endDate) return;

    updateUrlParams({
      filter: "custom",
      range: customRange,
      page: 1,
      limit: pagination.limit,
      search: searchTerm,
      column: searchColumn,
    });
  };

  const handleSearchSubmit = () => {
    updateUrlParams({
      filter: dateFilter,
      range: customRange,
      page: 1,
      limit: pagination.limit,
      search: searchTerm,
      column: searchColumn,
    });
  };

  const handleSearchClear = () => {
    setSearchTerm("");
    setSearchColumn("all");

    updateUrlParams({
      filter: dateFilter,
      range: customRange,
      page: 1,
      limit: pagination.limit,
      search: "",
      column: "all",
    });
  };

  const handlePageChange = (newPage) => {
    updateUrlParams({
      filter: dateFilter,
      range: customRange,
      page: newPage,
      limit: pagination.limit,
      search: searchTerm,
      column: searchColumn,
    });
  };

  const handleLimitChange = (newLimit) => {
    updateUrlParams({
      filter: dateFilter,
      range: customRange,
      page: 1,
      limit: newLimit,
      search: searchTerm,
      column: searchColumn,
    });
  };

  return (
    <>
      <MasterLayout>
        <Breadcrumb title={getStatusTitle()} />

        <div className="mt-4 mb-20">
          <div className="card p-3 border radius-8">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
              <div>
                <h6 className="mb-1">{getStatusTitle()}</h6>

                <p className="mb-0 text-secondary-light text-sm">
                  Current filter:{" "}
                  <span className="fw-semibold text-primary">
                    {dateFilter === "custom"
                      ? `${customRange.startDate || "-"} to ${
                          customRange.endDate || "-"
                        }`
                      : dateFilter}
                  </span>
                </p>
              </div>

              <div className="text-end">
                <span className="badge bg-primary">
                  {pagination.total || 0} Jobs
                </span>
              </div>
            </div>

            <div className="mt-3">
              <DashboardFilter
                filter={dateFilter}
                onFilterChange={handleFilterChange}
                customRange={customRange}
                setCustomRange={setCustomRange}
                onCustomApply={handleCustomApply}
                loading={loading}
              />
            </div>
          </div>
        </div>

        {loading && (
          <div className="text-center mt-4">
            <Spinner />
          </div>
        )}

        {!loading && (
          <AppointmentTable
            data={data}
            pagination={pagination}
            onPageChange={handlePageChange}
            onLimitChange={handleLimitChange}
            loading={loading}
            searchTerm={searchTerm}
            searchColumn={searchColumn}
            onSearchTermChange={setSearchTerm}
            onSearchColumnChange={setSearchColumn}
            onSearchSubmit={handleSearchSubmit}
            onSearchClear={handleSearchClear}
          />
        )}
      </MasterLayout>
    </>
  );
};

export default AppointmentStatusPage;